import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import styles from './AdminPanel.module.css'

interface TeamMeta {
  team_id: string
  team_name: string
  member_count: number
}

interface CompletionRow {
  tool_id: string
  team_id: string
  completed_count: number
}

interface StatsPayload {
  teams: TeamMeta[]
  completions: CompletionRow[]
}

interface ToolEntry {
  id: string
  label: string
  categoryLabel: string
}

function getAllTools(): ToolEntry[] {
  const out: ToolEntry[] = []
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    const cats = SECTIONS[sk].cats as unknown as Record<
      string,
      { label: string; tools: Record<string, { l: string }> }
    >
    for (const ck of Object.keys(cats)) {
      const cat = cats[ck]
      for (const tk of Object.keys(cat.tools)) {
        out.push({ id: tk, label: cat.tools[tk].l, categoryLabel: cat.label })
      }
    }
  }
  return out
}

export default function GlobalCompletionStats() {
  const [stats, setStats] = useState<StatsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function load() {
      setLoading(true)
      const { data, error: rpcError } = await supabase.rpc('get_global_completion_stats')
      if (rpcError) {
        setError(rpcError.message)
        setLoading(false)
        return
      }
      setStats(data as unknown as StatsPayload)
      setLoading(false)
    }
    load()
  }, [])

  const tools = useMemo(() => getAllTools(), [])

  // Map of `${tool_id}|${team_id}` -> completed_count
  const lookup = useMemo(() => {
    const m = new Map<string, number>()
    stats?.completions.forEach((c) => {
      m.set(`${c.tool_id}|${c.team_id}`, c.completed_count)
    })
    return m
  }, [stats])

  const totalMembers = useMemo(
    () => stats?.teams.reduce((sum, t) => sum + t.member_count, 0) ?? 0,
    [stats]
  )

  const teams = stats?.teams ?? []

  if (loading) {
    return (
      <div className={styles.card} data-testid="global-completion-stats-loading">
        <div className={styles.hdrDark}>Global Tool Completion (anonymised)</div>
        <div className={styles.loadingText}>Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.card} data-testid="global-completion-stats-error">
        <div className={styles.hdrDark}>Global Tool Completion (anonymised)</div>
        <div className={styles.loadingText}>Could not load stats: {error}</div>
      </div>
    )
  }

  if (teams.length === 0) {
    return (
      <div className={styles.card} data-testid="global-completion-stats-empty">
        <div className={styles.hdrDark}>Global Tool Completion (anonymised)</div>
        <div className={styles.loadingText}>No active teams yet.</div>
      </div>
    )
  }

  return (
    <div className={styles.card} data-testid="global-completion-stats">
      <div className={styles.hdrDark}>Global Tool Completion (anonymised)</div>
      <div className={styles.matrixWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Tool</th>
              {teams.map((t) => (
                <th key={t.team_id} data-testid={`gcs-team-header-${t.team_id}`}>
                  {t.team_name}
                </th>
              ))}
              <th data-testid="gcs-total-header">All teams</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((tool) => {
              const perTeamTotal = teams.reduce(
                (sum, t) => sum + (lookup.get(`${tool.id}|${t.team_id}`) ?? 0),
                0
              )
              return (
                <tr key={tool.id} data-testid={`gcs-row-${tool.id}`}>
                  <td>
                    <strong>{tool.label}</strong>
                    <div className={styles.emailCell}>{tool.categoryLabel}</div>
                  </td>
                  {teams.map((t) => {
                    const count = lookup.get(`${tool.id}|${t.team_id}`) ?? 0
                    return (
                      <td
                        key={t.team_id}
                        className={styles.actionCell}
                        data-testid={`gcs-cell-${tool.id}-${t.team_id}`}
                      >
                        {count} / {t.member_count}
                      </td>
                    )
                  })}
                  <td
                    className={styles.actionCell}
                    data-testid={`gcs-total-${tool.id}`}
                  >
                    <strong>
                      {perTeamTotal} / {totalMembers}
                    </strong>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
